import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import ContactFormAccordion from '../components/ContactFormAccordion.jsx'
import ResourceCard from '../components/resources/ResourceCard.jsx'
import SectionNav from '../components/resources/SectionNav.jsx'
import {
  AlertIcon,
  ChatIcon,
  DocumentIcon,
  HeartIcon,
  MailIcon,
  MapIcon,
  PhoneIcon,
  ShieldIcon
} from '../components/resources/ResourceIcons.jsx'

const Resources = () => {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const location = useLocation()

  usePageSeo({
    enabled: !isLoading,
    title: t('resources.meta.title'),
    description: t('resources.meta.description'),
    keywords: t('resources.meta.keywords'),
    pathname: location.pathname
  })

  const sections = useMemo(
    () => [
      { id: 'crisis', label: t('resources.crisis.title') },
      { id: 'specialized', label: t('resources.specialized.title') },
      { id: 'florida', label: t('resources.florida.title') },
      { id: 'support', label: t('resources.support.title') }
    ],
    [t, isLoading]
  )

  const crisisCards = useMemo(
    () => [
      {
        variant: 'emergency',
        title: t('resources.crisis.emergency.title'),
        number: t('resources.crisis.emergency.number'),
        tel: '911',
        description: t('resources.crisis.emergency.description'),
        icon: <AlertIcon className="w-5 h-5" />
      },
      {
        variant: 'crisis',
        title: t('resources.crisis.suicide.title'),
        number: t('resources.crisis.suicide.number'),
        tel: '988',
        description: t('resources.crisis.suicide.description'),
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        variant: 'crisis',
        title: t('resources.crisis.crisisText.title'),
        number: t('resources.crisis.crisisText.number'),
        description: t('resources.crisis.crisisText.description'),
        icon: <MailIcon className="w-5 h-5" />
      },
      {
        variant: 'crisis',
        title: t('resources.crisis.disaster.title'),
        number: t('resources.crisis.disaster.number'),
        tel: '18009855990',
        description: t('resources.crisis.disaster.description'),
        icon: <HeartIcon className="w-5 h-5" />
      },
      {
        variant: 'crisis',
        title: t('resources.crisis.veterans.title'),
        number: t('resources.crisis.veterans.number'),
        detail: t('resources.crisis.veterans.detail'),
        tel: '18002738255',
        description: t('resources.crisis.veterans.description'),
        icon: <ShieldIcon className="w-5 h-5" />
      }
    ],
    [t, isLoading]
  )

  const specializedCards = useMemo(
    () => [
      {
        title: t('resources.specialized.childAbuse.title'),
        number: t('resources.specialized.childAbuse.number'),
        detail: t('resources.specialized.childAbuse.text'),
        tel: '18004224453',
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.sexualAssault.title'),
        number: t('resources.specialized.sexualAssault.number'),
        tel: '18006564673',
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.domesticViolence.title'),
        number: t('resources.specialized.domesticViolence.number'),
        detail: t('resources.specialized.domesticViolence.text'),
        tel: '18007997233',
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.floridaDomesticViolence.title'),
        number: t('resources.specialized.floridaDomesticViolence.number'),
        detail: t('resources.specialized.floridaDomesticViolence.tty'),
        tel: '18005001119',
        icon: <MapIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.sexualViolence.title'),
        number: t('resources.specialized.sexualViolence.number'),
        tel: '18889567273',
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.runaway.title'),
        number: t('resources.specialized.runaway.number'),
        tel: '18007862929',
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.teenDating.title'),
        number: t('resources.specialized.teenDating.number'),
        detail: t('resources.specialized.teenDating.text'),
        tel: '18663319474',
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.humanTrafficking.title'),
        number: t('resources.specialized.humanTrafficking.number'),
        detail: t('resources.specialized.humanTrafficking.text'),
        tel: '18883737888',
        website: t('resources.specialized.humanTrafficking.website'),
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.floridaAbuse.title'),
        number: t('resources.specialized.floridaAbuse.number'),
        detail: t('resources.specialized.floridaAbuse.tty'),
        tel: '18009622873',
        icon: <MapIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.elderHelpline.title'),
        number: t('resources.specialized.elderHelpline.number'),
        detail: t('resources.specialized.elderHelpline.tty'),
        tel: '18009635337',
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.specialized.legalHotline.title'),
        number: t('resources.specialized.legalHotline.number'),
        tel: '18005001119',
        icon: <DocumentIcon className="w-5 h-5" />
      }
    ],
    [t, isLoading]
  )

  const floridaCards = useMemo(
    () => [
      {
        title: t('resources.florida.dcf.title'),
        description: t('resources.florida.dcf.description'),
        website: t('resources.florida.dcf.website'),
        icon: <DocumentIcon className="w-5 h-5" />
      },
      {
        title: t('resources.florida.reportAbuse.title'),
        website: t('resources.florida.reportAbuse.website'),
        icon: <AlertIcon className="w-5 h-5" />
      },
      {
        title: t('resources.florida.mobileResponse.title'),
        description: t('resources.florida.mobileResponse.description'),
        number: t('resources.florida.mobileResponse.palmBeach'),
        tel: '5616372102',
        website: t('resources.florida.mobileResponse.website'),
        icon: <PhoneIcon className="w-5 h-5" />
      }
    ],
    [t, isLoading]
  )

  const supportCards = useMemo(
    () => [
      {
        title: t('resources.support.nami.title'),
        number: t('resources.support.nami.number'),
        tel: '18009506264',
        email: t('resources.support.nami.email'),
        description: t('resources.support.nami.description'),
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.support.teenLine.title'),
        number: t('resources.support.teenLine.number'),
        detail: t('resources.support.teenLine.text'),
        tel: '8008528336',
        description: t('resources.support.teenLine.description'),
        icon: <ChatIcon className="w-5 h-5" />
      },
      {
        title: t('resources.support.warmLine.title'),
        number: t('resources.support.warmLine.number'),
        detail: t('resources.support.warmLine.hours'),
        tel: '18009451355',
        description: t('resources.support.warmLine.description'),
        icon: <PhoneIcon className="w-5 h-5" />
      },
      {
        title: t('resources.support.eldercare.title'),
        number: t('resources.support.eldercare.number'),
        tel: '18006771116',
        icon: <ChatIcon className="w-5 h-5" />
      }
    ],
    [t, isLoading]
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite [&_a]:no-underline [&_a:hover]:no-underline">
        <main>
          <div className="h-48 bg-primary-darkGreen/20 animate-pulse" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse space-y-4">
              <div className="h-10 bg-gray-200 rounded-lg w-2/3 mx-auto" />
              <div className="h-4 bg-gray-200 rounded w-full max-w-xl mx-auto" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-48 bg-gray-200 rounded-2xl" />
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite [&_a]:no-underline [&_a:hover]:no-underline">
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-primary-darkGreen to-primary-darkGreen/80 py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary-golden mb-3">
              {t('resources.ui.badge')}
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 text-balance">
              {t('resources.hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-3 text-balance">
              {t('resources.hero.subtitle')}
            </p>
            <p className="text-base text-white/75 max-w-3xl mx-auto leading-relaxed">
              {t('resources.hero.description')}
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="bg-amber-50 border-b border-amber-200/80">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-sm text-amber-950 text-center leading-relaxed">
              <span className="font-semibold">{t('resources.ui.disclaimerTitle')}</span>{' '}
              {t('resources.ui.disclaimer')}
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <SectionNav sections={sections} label={t('resources.ui.sectionNav')} />

          {/* Crisis */}
          <section id="crisis" className="scroll-mt-28 mb-16 lg:mb-20">
            <header className="mb-8 text-center max-w-3xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen mb-3">
                {t('resources.crisis.title')}
              </h2>
              <p className="text-gray-600 leading-relaxed">{t('resources.crisis.description')}</p>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {crisisCards.map((card) => (
                <ResourceCard key={card.title} {...card} />
              ))}
            </div>
          </section>

          {/* Specialized */}
          <section id="specialized" className="scroll-mt-28 mb-16 lg:mb-20">
            <header className="mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen">
                {t('resources.specialized.title')}
              </h2>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {specializedCards.map((card) => (
                <ResourceCard key={card.title} variant="specialized" {...card} />
              ))}
            </div>
          </section>

          {/* Florida */}
          <section id="florida" className="scroll-mt-28 mb-16 lg:mb-20">
            <header className="mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen">
                {t('resources.florida.title')}
              </h2>
            </header>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {floridaCards.map((card) => (
                <ResourceCard key={card.title} variant="florida" {...card} />
              ))}
            </div>
          </section>

          {/* Support */}
          <section id="support" className="scroll-mt-28">
            <header className="mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen">
                {t('resources.support.title')}
              </h2>
            </header>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
              {supportCards.map((card) => (
                <ResourceCard key={card.title} variant="support" {...card} />
              ))}
            </div>
          </section>
        </div>

        {/* CTA — contact accordion (same as home page) */}
        <section className="bg-gradient-to-br from-primary-darkGreen to-primary-darkGreen/80 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">{t('resources.cta.title')}</h2>
              <p className="text-base sm:text-lg text-white/90 leading-relaxed">
                {t('resources.cta.description')}
              </p>
            </div>
            <ContactFormAccordion
              source="resources_page"
              fieldIdPrefix="resources-"
              accordionId="resources-contact-accordion"
            />
          </div>
        </section>
      </main>
    </div>
  )
}

export default Resources
